<script>
  /**
   * Drag handle for adjusting block spacing
   */
  import { tick } from 'svelte';
  import { load_sounds, play_sound } from '#lib/handle_sounds.js';
  
  let {
    /** @type {HTMLElement} The block element to position relative to */
    anchor,
    /** @type {string} Current spacing value (for display) */
    spacing,
    /** @type {'top'|'bottom'} Position relative to block */
    position = 'bottom',
    /** @type {(value: string) => void} Callback when spacing changes */
    onchange,
  } = $props();

  // Spacing values in order (for drag mapping)
  const SPACING_VALUES = ['0', 'xs', 's', 'm', 'l'];
  
  let is_dragging = $state(false);
  let is_hovered = $state(false);
  let drag_start_y = $state(0);
  let drag_start_spacing = $state('m');
  let handle_ref = $state();
  let spacing_offset = $state(0);

  
  /** Handle visual center offset (padding + half line height) */
  const HANDLE_OFFSET = 10;

  /**
   * Get spacing index
   * @param {string} value
   * @returns {number}
   */
  function get_spacing_index(value) {
    const idx = SPACING_VALUES.indexOf(value);
    return idx >= 0 ? idx : 3; // default to 'm'
  }

  /**
   * Get computed spacing from the block's story-layout element
   * @returns {{ top: number, bottom: number }}
   */
  function get_computed_spacing() {
    if (!anchor) return { top: 0, bottom: 0 };
    const story_layout = anchor.querySelector('.story-layout');
    const styles = story_layout ? getComputedStyle(story_layout) : getComputedStyle(anchor);
    
    // Get padding values
    const padding_top = parseFloat(styles.paddingBlockStart) || parseFloat(styles.paddingTop) || 0;
    const padding_bottom = parseFloat(styles.paddingBlockEnd) || parseFloat(styles.paddingBottom) || 0;
    
    // Get margin values
    const margin_top = parseFloat(styles.marginBlockStart) || parseFloat(styles.marginTop) || 0;
    const margin_bottom = parseFloat(styles.marginBlockEnd) || parseFloat(styles.marginBottom) || 0;
    
    return {
      top: margin_top > 0 ? margin_top : padding_top,
      bottom: margin_bottom > 0 ? margin_bottom : padding_bottom,
    };
  }

  /**
   * Update position - centers handle in the spacing area
   */
  function update_position() {
    if (!anchor || !handle_ref) return;
    
    const computed = get_computed_spacing();
    const current_spacing = position === 'top' ? computed.top : computed.bottom;
    spacing_offset = current_spacing / 2 - HANDLE_OFFSET;
  }

  /**
   * Handle pointer down - start drag
   * @param {PointerEvent} event
   */
  function handle_pointerdown(event) {
    event.preventDefault();
    event.stopPropagation();
    
    // Load sounds on first interaction
    load_sounds();
    
    is_dragging = true;
    drag_start_y = event.clientY;
    drag_start_spacing = spacing || 'm';
    
    handle_ref?.setPointerCapture(event.pointerId);
  }

  /**
   * Handle pointer move - update spacing during drag
   * @param {PointerEvent} event
   */
  function handle_pointermove(event) {
    if (!is_dragging) return;
    
    const delta_y = event.clientY - drag_start_y;
    const start_index = get_spacing_index(drag_start_spacing);
    
    // Map drag distance to spacing steps (25px per step)
    const step_change = Math.round(delta_y / 25);
    const new_index = Math.max(0, Math.min(SPACING_VALUES.length - 1, start_index + step_change));
    const new_spacing = SPACING_VALUES[new_index];
    
    if (new_spacing !== spacing) {
      play_sound(new_index);
      onchange?.(new_spacing);
    }
  }

  /**
   * Handle pointer up - end drag
   * @param {PointerEvent} event
   */
  function handle_pointerup(event) {
    if (!is_dragging) return;
    is_dragging = false;
    handle_ref?.releasePointerCapture(event.pointerId);
  }

  // Update position when mounted
  $effect(() => {
    if (anchor && handle_ref) {
      tick().then(update_position);
    }
  });

  // Update position when spacing changes (e.g., from the other handle)
  $effect(() => {
    if (spacing && anchor && handle_ref) {
      tick().then(update_position);
    }
  });
</script>

{#if anchor}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    bind:this={handle_ref}
    class="spacing-handle {position}"
    class:dragging={is_dragging}
    class:visible={is_hovered || is_dragging}
    style="--spacing-offset: {spacing_offset}px;"
    onpointerdown={handle_pointerdown}
    onpointermove={handle_pointermove}
    onpointerup={handle_pointerup}
    onpointercancel={handle_pointerup}
    onmouseenter={() => is_hovered = true}
    onmouseleave={() => !is_dragging && (is_hovered = false)}
    role="slider"
    aria-label="Adjust block spacing ({position})"
    aria-valuenow={get_spacing_index(spacing)}
    aria-valuemin={0}
    aria-valuemax={SPACING_VALUES.length - 1}
    tabindex="-1"
  >
    <div class="handle-line"></div>
    {#if is_dragging}
      <span class="spacing-label">{spacing}</span>
    {/if}
  </div>
{/if}

<style>
  .spacing-handle {
    position: fixed;
    margin: 0;
    border: none;
    z-index: 100;
    
    /* CSS Anchor Positioning */
    position-anchor: --selected-block;
    left: anchor(center);
    transform: translateX(-50%);
    
    /* Layout */
    padding: 8px 24px;
    background: transparent;
    border-radius: var(--round);
    cursor: ns-resize;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.5;
    transition: opacity 0.15s ease;
    
    &.top {
      top: anchor(top);
      margin-top: var(--spacing-offset, 0px);
    }
    
    &.bottom {
      top: anchor(bottom);
      margin-top: calc(-1 * var(--spacing-offset, 0px) - 20px);
    }
    
    &.visible, &:hover, &:focus-visible {
      opacity: 1;
    }
    
    &.dragging {
      opacity: 1;
      cursor: grabbing;
    }
  }

  .handle-line {
    width: 40px;
    height: 4px;
    background: var(--editing);
    border-radius: 2px;
    box-shadow: 0 1px 3px oklch(0 0 0 / 0.2);
    transition: width 0.15s ease;
    
    .spacing-handle:hover &,
    .spacing-handle.dragging & {
      width: 56px;
    }
  }

  .spacing-label {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 2px 8px;
    background: var(--editing);
    color: white;
    font-size: 10px;
    font-weight: 600;
    border-radius: 4px;
    text-transform: uppercase;
    pointer-events: none;
    white-space: nowrap;
  }
</style>
